import { useCallback, useState, type SyntheticEvent } from 'react';
import type { WebsiteMediaFrameSize } from '@dukanti/shared';

export interface MediaNaturalSize {
  /** The intrinsic size of the shown file, once the browser knows it. */
  readonly size: WebsiteMediaFrameSize | null;
  /** The file could not be read (a video this browser cannot play, a gone image). */
  readonly failed: boolean;
  /** `onLoad` of an `<img>`. */
  readonly onImageLoad: (event: SyntheticEvent<HTMLImageElement>) => void;
  /** `onLoadedMetadata` of a `<video>`. */
  readonly onVideoMetadata: (event: SyntheticEvent<HTMLVideoElement>) => void;
  /** `onError` of either. */
  readonly onError: () => void;
}

interface Measured {
  readonly source: string | null;
  readonly size: WebsiteMediaFrameSize | null;
  readonly failed: boolean;
}

function positive(width: number, height: number): WebsiteMediaFrameSize | null {
  return width > 0 && height > 0 ? { width, height } : null;
}

/**
 * Epic #228 P6: the intrinsic size of the media the crop editor shows, read
 * from the element's own load events (an image's `naturalWidth/Height`, a
 * video's `videoWidth/Height` on `loadedmetadata`). The drag math needs it to
 * know how far the picture overflows its frame.
 *
 * The measurement belongs to one `source`: a new file starts unmeasured, so a
 * stale size never moves a different picture.
 */
export function useMediaNaturalSize(source: string | null): MediaNaturalSize {
  const [measured, setMeasured] = useState<Measured>({ source, size: null, failed: false });
  const current: Measured =
    measured.source === source ? measured : { source, size: null, failed: false };

  const onImageLoad = useCallback(
    (event: SyntheticEvent<HTMLImageElement>): void => {
      const { naturalWidth, naturalHeight } = event.currentTarget;
      setMeasured({ source, size: positive(naturalWidth, naturalHeight), failed: false });
    },
    [source],
  );
  const onVideoMetadata = useCallback(
    (event: SyntheticEvent<HTMLVideoElement>): void => {
      const { videoWidth, videoHeight } = event.currentTarget;
      setMeasured({ source, size: positive(videoWidth, videoHeight), failed: false });
    },
    [source],
  );
  const onError = useCallback((): void => {
    setMeasured({ source, size: null, failed: true });
  }, [source]);

  return { size: current.size, failed: current.failed, onImageLoad, onVideoMetadata, onError };
}
